/**
 * Lightweight connectivity probe that verifies actual server reachability.
 *
 * navigator.onLine is unreliable — it can report "online" behind captive
 * portals, dead WiFi, or when the server is down. This module does a real
 * HEAD request to /version.json (static, no auth, always available) to
 * confirm the server is reachable.
 *
 * Results are cached briefly (CACHE_TTL_MS) to avoid rapid-fire probes
 * when multiple events fire simultaneously (focus + online + visible).
 */

const PROBE_TIMEOUT_MS = 5000
const CACHE_TTL_MS = 5000

let cachedResult: boolean | null = null
let cachedAt = 0

/**
 * Probe whether the server is actually reachable by fetching a lightweight
 * static resource. Returns true if the server responds within the timeout,
 * false otherwise. Falls back to navigator.onLine if the probe itself errors
 * in an unexpected way.
 *
 * Results are cached for CACHE_TTL_MS to avoid hammering the server when
 * multiple sync triggers fire within the same window.
 */
export async function probeConnectivity(): Promise<boolean> {
  // Fast exit: browser says offline — no point probing
  if (!navigator.onLine) return false

  // Return cached result if fresh
  const now = Date.now()
  if (cachedResult !== null && now - cachedAt < CACHE_TTL_MS) {
    return cachedResult
  }

  try {
    const response = await fetch('/version.json', {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    })
    cachedResult = response.ok
    cachedAt = Date.now()
    return cachedResult
  } catch {
    // Network error or timeout — server unreachable
    cachedResult = false
    cachedAt = Date.now()
    return false
  }
}

/**
 * Invalidate the cached probe result. Call when a network event fires
 * (online, networkStatusChange) so the next probe does a real check.
 */
export function invalidateProbeCache(): void {
  cachedResult = null
  cachedAt = 0
}
